/**
 * Tổng quan — TK-25 (10/10/2026), dựng theo HÌNH LEE DUYỆT (`_BUS/ket_qua/TK-25_anh/MAU_TONG_QUAN_CHOT_v2.png`):
 *
 *   ① Dải sức khoẻ: một câu "hệ thống đang thế nào" + độ tươi số liệu + nút nhảy xuống ô vấn đề.
 *   ② 4 thẻ: Tổng số tủ (cột theo tuyến) · Tổng số tuyến đường (số màu nhấn) · Cảnh báo cần xem xét (vòng tủ có
 *      việc) · Số tủ online (vòng %). Đếm THEO TÀI KHOẢN — máy chủ lọc phạm vi (`/api/overview`), web chỉ vẽ.
 *   ③ Trái 5/12: Bản đồ tủ (chip tỉnh) + Tình trạng từng tuyến │ Phải 7/12: Vấn đề cần xem xét xử lý + Hoạt
 *      động gần đây (chỉ sự kiện có nghĩa — `HOAT_DONG_CO_NGHIA`).
 *
 * Ba nguyên tắc chép từ bảng điều khiển Versus (lee gửi 10/10, ghi ở `claude/hoc_mau_versus_tk25`):
 *   · Thiếu số thì nói "chưa có", không vẽ 0 (`Metric giaTri={null}`, Luật số 0).
 *   · Tuần yên ắng phải đọc là "yên ắng", không đọc là "hỏng": ô vấn đề TRỐNG vẫn vẽ, kèm giờ kiểm.
 *   · MỘT việc = MỘT hồ sơ: "lần đầu · lần cuối" + ba mốc Tạo → Ghi nhận → Đã xử lý (ai, lúc nào).
 *
 * Giữ từ GD-16/TK-24: L-MỘT-LẦN (thẻ số là LỐI VÀO, không vẽ lại số ở nơi thứ hai), L-KHÔNG-PHỒNG (ô vấn đề
 * cuộn trong khung 640 px), chữ người dùng (tệp 29 điều 6–7), không chữ diễn giải trên mặt trang (vào ⓘ).
 * ⚠ Màu đỏ CÓ MẶT ở đây, CHỈ cho mức 1 (KHẨN) — lee duyệt hình 10/10. Mất kết nối vẫn XÁM (tệp 29 L-NGÂN-SÁCH-MÀU).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Building2, CheckCheck, CheckCircle2, ChevronDown, PlugZap, RefreshCw, Route } from 'lucide-react';
import { api, errorMessage } from '../lib/api';
import { qk, useAlarms, useAlarmsDaDong, useHoatDongCoNghia, useOverview, useTree } from '../lib/queries';
import { phanMuc, xuLyChoTuXacNhan } from '../lib/canhbao';
import { CAU_KET_DONG_BO, cauDongBo, type ChieuDongBo } from '../lib/lechlich';
import { CoQuyen, useAuth } from '../lib/auth';
import { DauDangKiem, HuyHieuKhoa } from '../components/Khoa';
import { Button, Card, EmptyState, ErrorBox, Metric, Spinner, useToast } from '../components/ui';
import { ChiTietKyThuat } from '../components/ChiTietKyThuat';
import { BanDoTu } from '../components/BanDoTu';
import { dongHoatDong, HOAT_DONG_CO_NGHIA, soNgoRaLech, tenViec } from '../lib/loi_nguoi';
import { aiDaLam } from '../lib/nguonthaotac';
import type { Alarm, AuditRow, OverviewRouteCard, TreeRoute } from '../lib/types';
import { version as PHIEN_BAN } from '../../package.json';

const SO_HOAT_DONG = 6;          // Hoạt động gần đây: 6 sự kiện có nghĩa mới nhất, không phình
const SO_DA_XU_LY_HIEN = 5;      // "Đã xử lý · 7 ngày qua": 5 dòng, bấm mới mở thêm
const GIOI_HAN_HO_SO = 12;       // hồ sơ ĐANG MỞ vẽ ra trước nút "Xem thêm" (L-KHÔNG-PHỒNG trên điện thoại)
const TU_BAO_MOI_MS = 5 * 60_000; // "tủ liên lạc trong 5 phút qua" (ping 15 s cũng tính) — dải sức khoẻ

/** Chấm trạng thái bằng SVG ≤10px — KHÔNG phải nền. */
function Cham({ mau, px = 9 }: { mau: string; px?: number }) {
  return (
    <svg width={px} height={px} viewBox="0 0 10 10" aria-hidden="true" focusable="false" className={`shrink-0 ${mau}`}>
      <circle cx="5" cy="5" r="5" fill="currentColor" />
    </svg>
  );
}

/** Thanh mảnh 4 px (SVG) — nền `nen-2`, phần giá trị theo lớp màu trạng thái. */
function ThanhManh({ pt, lop }: { pt: number; lop: string }) {
  const w = Math.max(0, Math.min(100, pt));
  return (
    <svg width="100%" height="4" aria-hidden="true" focusable="false" className="block" data-thanh-manh>
      <rect x="0" y="0" width="100%" height="4" rx="2" className="text-nen-2" fill="currentColor" />
      {w > 0 && <rect x="0" y="0" width={`${w}%`} height="4" rx="2" className={lop} fill="currentColor" />}
    </svg>
  );
}

/**
 * Vòng phần trăm 64 px (SVG) — thẻ "Số tủ online" (xanh) và "Cảnh báo cần xem xét" (vàng), CÙNG một kiểu
 * (học Versus: bốn thẻ nhìn đồng bộ). `tu/mau` = phần tô; chữ giữa là `nhan` (vd "90%" hoặc "4/10").
 */
function VongTron({ tu, mau, nhan, lop }: { tu: number; mau: number; nhan: string; lop: string }) {
  const r = 26; const chuVi = 2 * Math.PI * r;
  const pt = mau > 0 ? Math.max(0, Math.min(1, tu / mau)) : 0;
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden="true" focusable="false" className="shrink-0" data-vong>
      <circle cx="32" cy="32" r={r} fill="none" strokeWidth="8" className="text-nen-3" stroke="currentColor" />
      {pt > 0 && (
        <circle cx="32" cy="32" r={r} fill="none" strokeWidth="8" strokeLinecap="round" className={lop} stroke="currentColor"
          strokeDasharray={`${chuVi * pt} ${chuVi}`} transform="rotate(-90 32 32)" />
      )}
      <text x="32" y="37" textAnchor="middle" fontSize="14" fontWeight="600" className="fill-chu-1" fill="currentColor">{nhan}</text>
    </svg>
  );
}

/**
 * Thẻ số theo tệp 29 §4 (icon 40 px · nhãn HOA 12 px · số Playfair 32 · dòng phụ) + ô `phai` (cột/vòng).
 * `null` ⇒ "chưa có số liệu" nghiêng xám qua `Metric` (Luật số 0), không vẽ "0".
 */
function TheSo({ icon, nhan, tri, donVi, phu, phai, onClick, dataTen, nhanMau = false }: {
  icon: React.ReactNode; nhan: string; tri: string | number | null | undefined;
  donVi?: string; phu?: React.ReactNode; phai?: React.ReactNode; onClick?: () => void; dataTen?: string;
  /** Số tô màu nhấn + viền nhấn (thẻ Tổng số tuyến đường — lee mục 3: "số có màu nổi bật"). */
  nhanMau?: boolean;
}) {
  const chuaRo = tri === null || tri === undefined || tri === '';
  const than = (
    <div className="flex items-center gap-3">
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-nut ${nhanMau ? 'bg-nhan/15 text-nhan' : 'bg-nen-2 text-chu-2'}`} aria-hidden="true">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-xs font-medium uppercase leading-snug tracking-wide text-chu-2">{nhan}</p>
        <div className="mt-1 flex items-baseline gap-1.5">
          {chuaRo
            ? <Metric giaTri={null} coNho />
            : <>
                <b data-so className={`chu-tieude text-[32px] font-semibold leading-none tabular-nums ${nhanMau ? 'text-nhan' : 'text-chu-1'}`}>{tri}</b>
                {donVi && <span className="text-[13px] text-chu-2">{donVi}</span>}
              </>}
        </div>
        {phu && <p className="m-0 mt-1.5 text-xs leading-snug text-chu-3">{phu}</p>}
      </div>
      {phai && <div className="shrink-0">{phai}</div>}
    </div>
  );
  const lop = `rounded-the border bg-nen-1 p-4 text-left ${nhanMau ? 'border-nhan/45' : 'border-vien'}`;
  return onClick
    ? <button type="button" data-the={dataTen} onClick={onClick} className={`${lop} block w-full transition hover:border-nhan/50`}>{than}</button>
    : <div data-the={dataTen} className={lop}>{than}</div>;
}

/* ---------------------------------------------------------------- helper giờ giấc */

/** "HH:mm dd/MM" — KHÔNG "trước". */
function gioNgan(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())} ${p(d.getDate())}/${p(d.getMonth() + 1)}`;
}
/** "HH:mm hôm nay" khi cùng ngày, còn lại "HH:mm dd/MM". */
function gioNguoi(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const nay = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const cungNgay = d.getFullYear() === nay.getFullYear() && d.getMonth() === nay.getMonth() && d.getDate() === nay.getDate();
  return cungNgay ? `${p(d.getHours())}:${p(d.getMinutes())} hôm nay` : gioNgan(iso);
}
/** Tuổi ngắn "N ngày / N giờ / N phút" — KHÔNG "trước". */
function tuoiNgan(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const ngay = Math.floor(s / 86400); if (ngay >= 1) return `${ngay} ngày`;
  const gio = Math.floor(s / 3600); if (gio >= 1) return `${gio} giờ`;
  return `${Math.max(1, Math.floor(s / 60))} phút`;
}
/** `HH:mm:ss` của một mốc ISO. */
function gioGiay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
/** Ngoặc "(N ngõ ra)" cho SCHEDULE_MISMATCH — N đếm từ câu gốc. Không đếm được ⇒ rỗng. */
function ngoacNgoRa(a: Alarm): string {
  if ((phanMuc(a.code).ma || a.code) !== 'SCHEDULE_MISMATCH') return '';
  const n = soNgoRaLech(a.message);
  return n ? ` · ${n} ngõ ra` : '';
}
/** Tên người để in: họ tên, không có thì tên đăng nhập; `null` = tài khoản đã xoá. */
function tenNguoi(u: { username: string; fullName: string } | null | undefined): string {
  if (!u) return 'tài khoản đã xoá';
  return u.fullName || u.username;
}

/* ---------------------------------------------------------------- trang */

export default function DashboardPage() {
  const overview = useOverview();
  const alarms = useAlarms();
  const daDong = useAlarmsDaDong(7);
  const tree = useTree();
  const qc = useQueryClient();
  const toast = useToast();
  const { coCoTuyen, daBietQuyen, coQuyen } = useAuth();
  const coNhatKy = coQuyen('tao_tai_khoan');
  const hoatDong = useHoatDongCoNghia(HOAT_DONG_CO_NGHIA, SO_HOAT_DONG, coNhatKy);

  const [tinhChon, setTinhChon] = useState<string | null>(null);   // chip tỉnh trên bản đồ
  useEffect(() => { document.title = 'Tổng quan — SDKD Chiếu sáng'; }, []);

  const moDuocTuyen = (routeId: string | null | undefined) =>
    !!routeId && coCoTuyen('canControl', routeId) === 'cho-qua';

  const lamMoiCanhBao = () => {
    qc.invalidateQueries({ queryKey: qk.alarms() });
    qc.invalidateQueries({ queryKey: qk.overview });
  };
  const ghiNhan = useMutation({
    mutationFn: (alarmId: string) => api.post(`/api/alarms/${alarmId}/ack`, {}),
    onSuccess: () => { toast.success('Đã ghi nhận — hồ sơ chuyển sang "Đang xử lý"'); lamMoiCanhBao(); },
    onError: (e) => toast.error('Không ghi nhận được', errorMessage(e)),
  });
  const xuLy = useMutation({
    mutationFn: (v: { id: string; ghiChu: string }) =>
      api.post<{ ketQua?: 'dong-ngay' | 'cho-tu-xac-nhan' }>(`/api/alarms/${v.id}/xu-ly`, v.ghiChu.trim() ? { ghiChu: v.ghiChu.trim() } : {}),
    onSuccess: (kq) => {
      toast.success(kq?.ketQua === 'cho-tu-xac-nhan'
        ? 'Đã ghi tên bạn — hồ sơ tự đóng khi tủ báo bình thường lại'
        : 'Đã đóng hồ sơ — ghi tên bạn và giờ');
      lamMoiCanhBao();
    },
    onError: (e) => toast.error('Không đánh dấu được', errorMessage(e)),
  });
  /* TK-26 — HAI CHIỀU ĐỒNG BỘ (lee 10/10). Chiều 2: máy chủ → tủ (đường đẩy lịch sẵn có, ĐỔI đèn). */
  const dayLich = useMutation({
    mutationFn: (cabinetId: string) => api.post(`/api/cabinets/${cabinetId}/schedule/apply`, { acceptLosses: false }),
    onSuccess: () => { toast.success('Đã gửi lịch máy chủ xuống tủ', CAU_KET_DONG_BO['may-chu-xuong-tu']); lamMoiCanhBao(); },
    onError: (e) => toast.error('Không đẩy được lịch', errorMessage(e)),
  });
  /* Chiều 1: tủ → máy chủ (đọc ba ngõ trong tủ làm bản máy chủ; đèn KHÔNG đổi). Máy chủ trả câu "đã đổi gì" từ dữ liệu thật. */
  const layLich = useMutation({
    mutationFn: (cabinetId: string) =>
      api.post<{ ok: boolean; thayDoi: string[]; ghiChu: string }>(`/api/cabinets/${cabinetId}/schedule/lay-tu-tu`, {}),
    onSuccess: (kq) => {
      const chiTiet = kq.thayDoi.length ? kq.thayDoi.join(' · ') : undefined;
      if (kq.ok) toast.success(kq.ghiChu, chiTiet); else toast.warning(kq.ghiChu, chiTiet);
      lamMoiCanhBao();
    },
    onError: (e) => toast.error('Không lấy được lịch từ tủ', errorMessage(e)),
  });

  const o = overview.data;

  /** Tên tuyến theo routeId. */
  const tenTuyen = useMemo(() => {
    const m = new Map<string | null, string>();
    for (const r of o?.byRoute ?? []) m.set(r.routeId, r.tenDayDu);
    for (const r of tree.data ?? []) if (!m.has(r.id)) m.set(r.id, r.tenDayDu);
    return m;
  }, [o?.byRoute, tree.data]);

  /** Tên tủ theo id (Hoạt động gần đây). */
  const tenTu = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of tree.data ?? []) for (const c of r.cabinets ?? []) m.set(c.id, c.name);
    return m;
  }, [tree.data]);

  /** Số việc đang mở theo tuyến (Tình trạng từng tuyến). */
  const viecTheoTuyen = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of alarms.data ?? []) {
      const rid = a.cabinet?.routeId;
      if (rid) m.set(rid, (m.get(rid) ?? 0) + 1);
    }
    return m;
  }, [alarms.data]);

  /**
   * Mức: Khẩn (mức 1) · Cảnh báo (mức 2) · Mất kết nối (xám — tệp 29 L-NGÂN-SÁCH-MÀU) — đếm trên danh sách
   * đang mở đã tải. Không có mức "nhắc nhở": dữ liệu chỉ có hai mức, bịa mức thứ ba là phân loại giả.
   */
  const theoMuc = useMemo(() => {
    let khan = 0, canh = 0, mat = 0, moi = 0, dangXuLy = 0, choTu = 0;
    const tuCoViec = new Set<string>();
    for (const a of alarms.data ?? []) {
      const loai = loaiMuc(a);
      if (loai === 'khan') khan++; else if (loai === 'mat') mat++; else canh++;
      const tt = trangThaiHoSo(a);
      if (tt === 'moi') moi++; else if (tt === 'dang-xu-ly') dangXuLy++; else if (tt === 'cho-tu') choTu++;
      tuCoViec.add(a.cabinetId);
    }
    return { khan, canh, mat, moi, dangXuLy, choTu, soTuCoViec: tuCoViec.size };
  }, [alarms.data]);

  /** Độ tươi từ cây: tủ báo trong 5 phút · tủ im lâu nhất. Không có cây ⇒ null (không bịa). */
  const doTuoi = useMemo(() => {
    const rs = tree.data;
    if (!Array.isArray(rs)) return null;
    const nguong = Date.now() - TU_BAO_MOI_MS;
    let baoMoi = 0, tong = 0;
    let imLauNhat: { ten: string; luc: string } | null = null;
    for (const r of rs) for (const c of r.cabinets ?? []) {
      tong++;
      if (c.lastSeenAt && new Date(c.lastSeenAt).getTime() >= nguong) baoMoi++;
      if (c.status !== 'ONLINE' && c.lastSeenAt && (!imLauNhat || c.lastSeenAt < imLauNhat.luc)) imLauNhat = { ten: c.name, luc: c.lastSeenAt };
    }
    return { baoMoi, tong, imLauNhat };
  }, [tree.data]);

  /** Chip tỉnh cho bản đồ: tên tỉnh → số tủ (từ cây; tuyến không có tỉnh gom vào "Chưa rõ tỉnh"). */
  const theoTinh = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of tree.data ?? []) {
      const k = r.tinh?.trim() || 'Chưa rõ tỉnh';
      m.set(k, (m.get(k) ?? 0) + (r.cabinets?.length ?? 0));
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [tree.data]);
  const diemKhung = useMemo<[number, number][] | null>(() => {
    if (!tinhChon) return null;
    const ds: [number, number][] = [];
    for (const r of tree.data ?? []) {
      if ((r.tinh?.trim() || 'Chưa rõ tỉnh') !== tinhChon) continue;
      for (const c of r.cabinets ?? []) if (typeof c.lat === 'number' && typeof c.lng === 'number' && c.lat !== 0 && c.lng !== 0) ds.push([c.lat, c.lng]);
    }
    return ds;
  }, [tinhChon, tree.data]);

  /**
   * NHÓM Tuyến → Tủ → Vấn đề (lee mục 6). Tuyến có việc KHẨN lên đầu, rồi tuyến có việc lâu nhất; trong tủ,
   * việc mới nhất lên trên (lee: "vấn đề mới nằm phía trên").
   */
  const nhomTuyen = useMemo(() => {
    type NhomTu = { cabinetId: string; ten: string; ma: string | null; items: Alarm[] };
    type NhomTuyen = { routeId: string | null; ten: string; tus: Map<string, NhomTu>; soViec: number; coKhan: boolean; cu: number };
    const m = new Map<string | null, NhomTuyen>();
    for (const a of alarms.data ?? []) {
      const rid = a.cabinet?.routeId ?? null;
      const g = m.get(rid) ?? { routeId: rid, ten: (rid && tenTuyen.get(rid)) || 'Chưa gán tuyến', tus: new Map(), soViec: 0, coKhan: false, cu: Infinity };
      const t = g.tus.get(a.cabinetId) ?? { cabinetId: a.cabinetId, ten: a.cabinet?.name ?? a.cabinetId, ma: a.cabinet?.code ?? null, items: [] };
      t.items.push(a);
      g.tus.set(a.cabinetId, t);
      g.soViec++;
      if (loaiMuc(a) === 'khan') g.coKhan = true;
      g.cu = Math.min(g.cu, new Date(a.raisedAt).getTime());
      m.set(rid, g);
    }
    const ds = [...m.values()].map((g) => ({
      ...g,
      tus: [...g.tus.values()].map((t) => ({ ...t, items: [...t.items].sort((x, y) => new Date(y.raisedAt).getTime() - new Date(x.raisedAt).getTime()) })),
    }));
    return ds.sort((x, y) => Number(y.coKhan) - Number(x.coKhan) || x.cu - y.cu);
  }, [alarms.data, tenTuyen]);

  /*
   * L-KHÔNG-PHỒNG ở MỌI bề rộng: điện thoại không có khung cuộn (cuộn lồng nuốt hồ sơ cuối), nên trần là SỐ hồ sơ vẽ ra —
   * `GIOI_HAN_HO_SO` đầu tiên theo đúng thứ tự (tuyến khẩn → lâu nhất), còn lại sau nút "Xem thêm N hồ sơ" (nói ra số bị
   * giấu). Đo ảnh tk25_b bộ B (100 tủ): không trần ⇒ trang 17 392 px ở 390.
   */
  const [hienHet, setHienHet] = useState(false);
  const { nhomCat, soAn } = useMemo(() => {
    if (hienHet) return { nhomCat: nhomTuyen, soAn: 0 };
    let con = GIOI_HAN_HO_SO; let an = 0;
    const ra: typeof nhomTuyen = [];
    for (const g of nhomTuyen) {
      const tus: typeof g.tus = [];
      for (const t of g.tus) {
        if (con <= 0) { an += t.items.length; continue; }
        const lay = t.items.slice(0, con); an += t.items.length - lay.length; con -= lay.length;
        tus.push({ ...t, items: lay });
      }
      if (tus.length) ra.push({ ...g, tus });
    }
    return { nhomCat: ra, soAn: an };
  }, [nhomTuyen, hienHet]);

  const soTuyen = o ? (o.soTuyen ?? o.byRoute.filter((r) => r.routeId !== null).length) : null;
  /** Tủ chưa gán tuyến (chỉ SUPER_ADMIN thấy) — TẬP CON của tổng, nêu ở dòng phụ, không % (GD-16 mục 2). */
  const chuaGan = o ? ((o.byRoute.find((r) => r.routeId === null)?.total) ?? 0) : 0;
  const cuonToi = (id: string) => () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  /** Dải sức khoẻ: ổn định khi không việc mở và không tủ im. */
  const onDinh = !!o && o.activeAlarms === 0 && o.offline === 0;
  const chiTietDau = o
    ? `${o.online}/${o.cabinets} tủ đang nối · ${o.activeAlarms === 0 ? 'không có vấn đề đang mở' : `${o.activeAlarms} vấn đề đang mở${theoMuc.khan > 0 ? ` (${theoMuc.khan} khẩn)` : ''}`}`
    : '';

  return (
    <div className="tq-luoi space-y-4" data-trang-tong-quan>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="chu-tieude m-0 text-[20px] font-semibold leading-tight text-chu-1">Tổng quan</h1>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            icon={<RefreshCw className="h-4 w-4" />}
            onClick={() => { void overview.refetch(); void alarms.refetch(); void daDong.refetch(); void tree.refetch(); }}
            loading={overview.isFetching}
          >
            Làm mới
          </Button>
        </div>
      </div>

      {overview.isLoading && <Spinner label="Đang tải số liệu tổng quan…" />}
      {overview.isError && <ErrorBox error={overview.error} onRetry={() => void overview.refetch()} />}

      {o && (
        <>
          {/* ① DẢI SỨC KHOẺ — một câu, độ tươi, lối vào ô vấn đề */}
          <div
            data-dai-suc-khoe={onDinh ? 'on-dinh' : 'can-chu-y'}
            className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-the border bg-nen-1 px-4 py-2.5 ${onDinh ? 'border-nhan/40' : 'border-canh/40'}`}
          >
            {/* ⛔ Ca yên KHÔNG tô lá ([95]): mọi tủ đang nối + 0 hồ sơ mở là "không có việc", chưa phải "đã đo là đúng". */}
            <span className={`inline-flex items-center gap-2 rounded-thuoc border px-3 py-1 text-xs font-semibold tracking-wide ${onDinh ? 'border-nhan/50 bg-nhan/10 text-nhan' : 'border-canh/50 bg-canh-nen text-canh'}`}>
              <Cham mau={onDinh ? 'text-nhan' : 'text-canh'} px={8} />
              {onDinh ? 'KHÔNG CÓ VIỆC MỞ' : 'CẦN CHÚ Ý'}
            </span>
            <span className="text-sm text-chu-1" data-cau-suc-khoe>{chiTietDau}</span>
            <span className="ml-auto flex flex-wrap items-center gap-x-3 text-xs text-chu-3" data-do-tuoi>
              {doTuoi && (
                <>
                  <span>Số liệu: <b className="font-medium text-chu-2">{doTuoi.baoMoi}/{doTuoi.tong} tủ liên lạc trong 5 phút qua</b></span>
                  {doTuoi.imLauNhat && <span className="text-canh">{doTuoi.imLauNhat.ten} im từ {gioNguoi(doTuoi.imLauNhat.luc)}</span>}
                </>
              )}
              {o.lucLayMau && <span>lấy mẫu <b className="font-medium text-chu-2 tabular-nums">{gioGiay(o.lucLayMau)}</b></span>}
            </span>
            <Button size="sm" className="min-h-[36px] sm:min-h-0" onClick={cuonToi('van-de')}>Xem vấn đề ↓</Button>
          </div>

          {/* ② BỐN THẺ SỐ — theo tài khoản; mỗi thẻ là LỐI VÀO */}
          <div data-vung="tong-quan" className="tq-hang tq-so">
            <TheSo dataTen="tong-tu" icon={<Building2 className="h-5 w-5" />} nhan="Tổng số tủ" tri={o.cabinets}
              phu={o.cabinets === 0 ? 'Chưa có tủ nào' : `tài khoản này quản lý · ${soTuyen ?? '—'} tuyến${chuaGan > 0 ? ` · ${chuaGan} chưa gán tuyến` : ''}`}
              phai={o.cabinets > 0 ? <CotTheoTuyen ds={o.byRoute} /> : undefined}
              onClick={cuonToi('ban-do-tu')} />
            <TheSo dataTen="tong-tuyen" nhanMau icon={<Route className="h-5 w-5" />} nhan="Tổng số tuyến đường" tri={soTuyen}
              phu={soTuyen === 0 ? 'Chưa có tuyến nào' : 'tài khoản này quản lý'}
              onClick={cuonToi('theo-tuyen')} />
            <TheSo dataTen="canh-bao" icon={<AlertTriangle className="h-5 w-5" />} nhan="Cảnh báo cần xem xét" tri={o.activeAlarms}
              phu={o.activeAlarms === 0
                ? `không có việc đang mở${o.lucLayMau ? ` · kiểm lúc ${gioGiay(o.lucLayMau).slice(0, 5)}` : ''}`
                : [`${theoMuc.moi} mới`, `${theoMuc.dangXuLy} đang xử lý`, theoMuc.choTu > 0 ? `${theoMuc.choTu} chờ tủ xác nhận` : null,
                   `${theoMuc.soTuCoViec}/${o.cabinets} tủ có việc`].filter(Boolean).join(' · ')}
              phai={<VongTron tu={theoMuc.soTuCoViec} mau={o.cabinets} nhan={`${theoMuc.soTuCoViec}/${o.cabinets}`} lop="text-canh" />}
              onClick={cuonToi('van-de')} />
            <TheSo dataTen="tu-online" icon={<PlugZap className="h-5 w-5" />} nhan="Số tủ online" tri={o.online} donVi={`/ ${o.cabinets}`}
              phu={o.cabinets === 0 ? undefined : <><b className="font-medium text-tot">{o.online} đang nối</b> · {o.offline === 0 ? 'không tủ nào im' : `${o.offline} mất kết nối`}</>}
              phai={<VongTron tu={o.online} mau={o.cabinets} nhan={o.cabinets > 0 ? `${Math.round((o.online / o.cabinets) * 100)}%` : '—'} lop="text-tot" />} />
          </div>

          {/* ③ THÂN: trái Bản đồ + Tình trạng tuyến │ phải Vấn đề + Hoạt động */}
          <div className="tq-hang tq-than" data-hang="2">
            <div className="tq-than-trai">
              <Card
                id="ban-do-tu"
                title="Bản đồ tủ"
                className="min-w-0"
                goiY={{ nhan: 'Bản đồ tủ', noiDung: 'Tủ có toạ độ mới có chấm (tủ tự đo hoặc nhập tay — popup nói nguồn). Nhiều tủ gần nhau gom thành một cụm có số; bấm cụm để phóng vào, bấm chip tỉnh để ôm khung về tỉnh đó. Bấm một chấm để xem tủ.' }}
                actions={theoTinh.length > 1 ? (
                  <div className="flex flex-wrap gap-1.5" data-chip-tinh>
                    <button type="button" onClick={() => setTinhChon(null)}
                      className={`min-h-[36px] rounded-thuoc border px-2.5 py-1 text-xs sm:min-h-0 ${tinhChon === null ? 'border-nhan bg-nen-2 text-chu-1' : 'border-vien text-chu-2 hover:border-nhan/50'}`}>
                      Tất cả · {o.cabinets}
                    </button>
                    {theoTinh.map(([tinh, n]) => (
                      <button key={tinh} type="button" onClick={() => setTinhChon((cu) => (cu === tinh ? null : tinh))}
                        className={`min-h-[36px] rounded-thuoc border px-2.5 py-1 text-xs sm:min-h-0 ${tinhChon === tinh ? 'border-nhan bg-nen-2 text-chu-1' : 'border-vien text-chu-2 hover:border-nhan/50'}`}>
                        {tinh} · {n}
                      </button>
                    ))}
                  </div>
                ) : undefined}
              >
                <BanDoTu cay={tree.data} canhBao={alarms.data} caoLop="h-[460px]" diemKhung={diemKhung} />
              </Card>

              <Card
                id="theo-tuyen"
                title="Tình trạng từng tuyến"
                className="min-w-0"
                goiY={{ nhan: 'Tình trạng từng tuyến', noiDung: 'Mỗi dòng: tủ đang nối / tổng, số việc đang mở, thanh tỷ lệ nối. Số ngõ ra bật là tủ tự khai, chỉ đếm trên tủ đang nối. Bấm dòng để mở màn hình điều khiển toàn tuyến (chỉ tuyến bạn được gán).' }}
                actions={<Link to="/dieu-khien" className="text-sm text-nhan hover:underline">Điều khiển →</Link>}
              >
                {o.byRoute.length === 0 ? (
                  <EmptyState
                    icon={<Building2 className="h-8 w-8" />}
                    title="Chưa có tuyến đường nào"
                    hint="Quản trị viên cần tạo tuyến và khai báo tủ trước khi vận hành."
                  />
                ) : (
                  <ul className="cuon-luoi-tuyen grid m-0 list-none grid-cols-1 divide-y divide-vien p-0" data-ds-tuyen>
                    {o.byRoute.map((r) =>
                      moDuocTuyen(r.routeId) ? (
                        <li key={r.routeId}>
                          <Link to={`/dieu-khien?tuyen=${r.routeId}`} className="block rounded-nut px-1 py-2.5 transition hover:bg-nen-2">
                            <RouteCard route={r} soViec={r.routeId ? (viecTheoTuyen.get(r.routeId) ?? 0) : 0} />
                          </Link>
                        </li>
                      ) : (
                        <li key={r.routeId ?? '(chua-gan-duong)'} className="px-1 py-2.5">
                          <RouteCard route={r} soViec={r.routeId ? (viecTheoTuyen.get(r.routeId) ?? 0) : 0} />
                          {r.routeId && (daBietQuyen
                            ? <HuyHieuKhoa className="mt-2" />
                            : <DauDangKiem className="mt-2" />)}
                        </li>
                      )
                    )}
                  </ul>
                )}
              </Card>
            </div>

            <div className="tq-than-phai">
              <Card
                id="van-de"
                title="Vấn đề cần xem xét xử lý"
                className="min-w-0"
                bodyClass="p-0"
                goiY={{ nhan: 'Vấn đề cần xem xét xử lý', noiDung: 'Mỗi việc là MỘT hồ sơ, xếp theo tuyến → tủ. "Ghi nhận" = đang có người lo. "Đã xử lý" ghi tên người bấm và giờ; việc tủ tự báo được (lịch, công tơ, GPS, mất kết nối…) thì hồ sơ chờ tủ báo bình thường lại rồi mới đóng. Việc đã xử lý trong 7 ngày nằm cuối, chữ xanh.' }}
                actions={
                  <span className="flex flex-wrap gap-1.5 text-xs">
                    <span className="rounded-thuoc border border-nhan bg-nen-2 px-2.5 py-1 text-chu-1" data-chip-dang-mo>
                      {/* [66] ①: danh sách tải ít hơn tổng ⇒ nói CẢ HAI số; bằng nhau ⇒ một số. */}
                      {alarms.data && alarms.data.length !== o.activeAlarms
                        ? `Đang mở · ${alarms.data.length} / ${o.activeAlarms} hồ sơ`
                        : `Đang mở · ${o.activeAlarms} hồ sơ`}
                    </span>
                    <button type="button" onClick={cuonToi('da-xu-ly')} className="min-h-[36px] rounded-thuoc border border-vien px-2.5 py-1 text-chu-2 hover:border-nhan/50 sm:min-h-0" data-chip-da-xu-ly>
                      Đã xử lý 7 ngày · {daDong.data ? daDong.data.length : '—'}
                    </button>
                  </span>
                }
              >
                {alarms.isLoading && <div className="p-4"><Spinner label="Đang tải hồ sơ…" /></div>}
                {alarms.isError && <div className="p-4"><ErrorBox error={alarms.error} onRetry={() => void alarms.refetch()} /></div>}
                {alarms.data && (
                  <div className="tq-cuon-van-de">
                    {/* thanh phân mức + chú giải — nhìn là biết hôm nay nặng hay nhẹ */}
                    <div className="flex items-center gap-3 px-4 pt-3" data-thanh-muc>
                      <svg width="100%" height="7" aria-hidden="true" focusable="false" className="block min-w-0 flex-1">
                        <rect x="0" y="0" width="100%" height="7" rx="3.5" className="text-nen-2" fill="currentColor" />
                        {(() => {
                          const n = alarms.data.length; if (n === 0) return null;
                          const doan = [
                            { so: theoMuc.khan, lop: 'text-nguy' }, { so: theoMuc.canh, lop: 'text-canh' }, { so: theoMuc.mat, lop: 'text-chu-3' },
                          ];
                          let x = 0;
                          return doan.map((d, i) => {
                            if (d.so === 0) return null;
                            const w = (d.so / n) * 100; const el = <rect key={i} x={`${x}%`} y="0" width={`${w}%`} height="7" rx="3.5" className={d.lop} fill="currentColor" />;
                            x += w; return el;
                          });
                        })()}
                      </svg>
                      <span className="flex shrink-0 flex-wrap gap-x-3 text-xs text-chu-2">
                        <span className="inline-flex items-center gap-1"><Cham mau="text-nguy" px={8} />Khẩn <b className="text-chu-1">{theoMuc.khan}</b></span>
                        <span className="inline-flex items-center gap-1"><Cham mau="text-canh" px={8} />Cảnh báo <b className="text-chu-1">{theoMuc.canh}</b></span>
                        {theoMuc.mat > 0 && <span className="inline-flex items-center gap-1"><Cham mau="text-chu-3" px={8} />Mất kết nối <b className="text-chu-1">{theoMuc.mat}</b></span>}
                      </span>
                    </div>

                    {alarms.data.length === 0 ? (
                      <div className="px-4 py-8 text-center" data-khong-viec>
                        {/* ⛔ KHÔNG màu lá ([95]): "không hồ sơ nào mở" chỉ nói không có sự cố NÀO ĐƯỢC BÁO, không nói hệ đang chạy đúng. */}
                        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full border border-nhan/40 bg-nhan/10 text-nhan"><CheckCircle2 className="h-7 w-7" /></span>
                        <p className="m-0 mt-3 text-base font-semibold text-chu-1">Không có vấn đề nào đang mở</p>
                        <p className="m-0 mt-1 text-sm text-chu-3">
                          Đã kiểm {o.cabinets} tủ{o.lucLayMau ? ` lúc ${gioGiay(o.lucLayMau)}` : ''}
                          {daDong.data && daDong.data[0]?.clearedAt ? ` · việc gần nhất đã đóng ${gioNguoi(daDong.data[0].clearedAt)}` : ''}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2.5 p-4" data-ds-van-de>
                        {nhomCat.map((g) => (
                          <section key={g.routeId ?? 'chua-gan'} className="overflow-hidden rounded-nut border border-vien" data-nhom-tuyen={g.routeId ?? 'chua-gan'}>
                            <header className="flex items-center gap-2 bg-nen-2 px-3 py-2 text-sm font-semibold text-chu-1">
                              <span className="min-w-0 truncate" title={g.ten}>{g.ten}</span>
                              <span className="ml-auto shrink-0 text-xs font-normal text-chu-3">{g.soViec} vấn đề · {g.tus.length} tủ</span>
                            </header>
                            <div className="px-3 pb-2">
                              {g.tus.map((t) => (
                                <div key={t.cabinetId} data-nhom-tu={t.cabinetId}>
                                  <p className="m-0 pt-2 text-xs font-medium text-chu-2">
                                    <Link to={`/tu/${t.cabinetId}`} className="inline-flex min-h-[36px] items-center text-nhan hover:underline sm:min-h-0">Tủ {t.ten}</Link>
                                    {t.ma && <span className="ml-1 text-chu-3">({t.ma})</span>}
                                  </p>
                                  {t.items.map((a) => (
                                    <DongVanDe
                                      key={a.id}
                                      a={a}
                                      dayLichDuoc={!!a.cabinet?.routeId && coCoTuyen('canEditSchedule', a.cabinet.routeId) === 'cho-qua'}
                                      onGhiNhan={() => ghiNhan.mutate(a.id)}
                                      onXuLy={(ghiChu) => xuLy.mutate({ id: a.id, ghiChu })}
                                      onDayLich={() => dayLich.mutate(a.cabinetId)}
                                      onLayLich={() => layLich.mutate(a.cabinetId)}
                                      dangBan={(ghiNhan.isPending && ghiNhan.variables === a.id) || (xuLy.isPending && xuLy.variables?.id === a.id)
                                        || (dayLich.isPending && dayLich.variables === a.cabinetId) || (layLich.isPending && layLich.variables === a.cabinetId)}
                                    />
                                  ))}
                                </div>
                              ))}
                            </div>
                          </section>
                        ))}
                        {soAn > 0 && (
                          <button type="button" onClick={() => setHienHet(true)} data-xem-them-ho-so
                            className="min-h-[36px] w-full rounded-nut border border-vien px-3 py-1.5 text-sm text-nhan hover:border-nhan/50">
                            Xem thêm {soAn} hồ sơ
                          </button>
                        )}
                        {o.activeAlarms > alarms.data.length && (
                          <p className="m-0 text-xs text-chu-3">Còn <b>{o.activeAlarms - alarms.data.length}</b> hồ sơ nữa chưa tải vào danh sách.</p>
                        )}
                      </div>
                    )}

                  </div>
                )}
                {alarms.data && (
                  <div data-duoi-khung-cuon>
                    {/* ĐÃ XỬ LÝ · 7 NGÀY QUA — dồn cuối, chữ xanh (lee 10/10); NGOÀI khung cuộn để luôn thấy */}
                    <DaXuLy id="da-xu-ly" ds={daDong.data} dangTai={daDong.isLoading} />

                    {/* Chi tiết kỹ thuật (chỉ Quản trị): mã + nguyên văn — L-CHỮ-NGƯỜI-DÙNG */}
                    {alarms.data.length > 0 && (
                      <div className="px-4 pb-4">
                        <ChiTietKyThuat>
                          <div className="tbl-wrap cuon-doc">
                            <table className="tbl tbl-cuon table-fixed">
                              <colgroup><col style={{ width: '18%' }} /><col style={{ width: '24%' }} /><col style={{ width: '58%' }} /></colgroup>
                              <thead><tr><th className="text-xs uppercase">Tủ</th><th className="text-xs uppercase">Mã lỗi</th><th className="text-xs uppercase">Nguyên văn</th></tr></thead>
                              <tbody>
                                {alarms.data.map((a) => (
                                  <tr key={a.id}>
                                    <td className="text-chu-2">{a.cabinet?.code ?? a.cabinetId}</td>
                                    <td className="so-do text-chu-1">{a.code}</td>
                                    <td className="truncate text-chu-2" title={a.message ?? ''}>{a.message ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </ChiTietKyThuat>
                      </div>
                    )}
                  </div>
                )}
              </Card>

              {coNhatKy && (
                <TheThuGon
                  khoa="kapp.tq.hoatDong"
                  title="Hoạt động gần đây"
                  macDinhMo
                  phu={hoatDong.data ? `${Math.min(SO_HOAT_DONG, hoatDong.data.length)} sự kiện mới nhất` : undefined}
                  actions={<Link to="/nhat-ky" className="text-sm text-nhan hover:underline">Xem tất cả →</Link>}
                >
                  <HoatDongGanDay ds={hoatDong.data} dangTai={hoatDong.isLoading} loi={hoatDong.isError} tenTu={tenTu} />
                </TheThuGon>
              )}
            </div>
          </div>

          <p className="m-0 border-t border-vien pt-3 text-xs text-chu-3" data-chan-trang>SDKD Chiếu sáng v{PHIEN_BAN}</p>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- hồ sơ vấn đề */

/** Nhóm màu của một hồ sơ: khẩn (mức 1, đỏ) · canh (mức 2, vàng) · mat (mất kết nối, xám — tệp 29). */
type LoaiMuc = 'khan' | 'canh' | 'mat';
function loaiMuc(a: Alarm): LoaiMuc {
  const pm = phanMuc(a.code, a.mucDo);
  if ((pm.ma || a.code) === 'MAT_KET_NOI') return 'mat';
  return pm.mucDo === 1 ? 'khan' : 'canh';
}

/**
 * Trạng thái hồ sơ ĐANG MỞ, suy từ các mốc (không có cột trạng thái riêng — một sự thật, một chỗ):
 *   moi        chưa ai ghi nhận
 *   dang-xu-ly đã ghi nhận, chưa ai bấm "Đã xử lý"
 *   cho-tu     đã bấm "Đã xử lý" (lời khai), nguồn CHƯA báo hết — hồ sơ còn mở
 */
type TrangThaiHoSo = 'moi' | 'dang-xu-ly' | 'cho-tu';
function trangThaiHoSo(a: Alarm): TrangThaiHoSo {
  if (a.resolvedAt) return 'cho-tu';
  return a.ackedAt ? 'dang-xu-ly' : 'moi';
}


const LOP_VIEN: Record<LoaiMuc, string> = { khan: 'border-nguy', canh: 'border-canh', mat: 'border-chu-3' };
/** Nút trên điện thoại cao ≥ 36 px (tệp 29: vùng chạm); từ `sm` trở lên về cỡ gọn. Đo ảnh tk25_b/390: nút `sm` = 30 px. */
const NUT_DT = 'min-h-[36px] sm:min-h-0';

/**
 * MỘT DÒNG HỒ SƠ: viền trái theo mức · tên việc + nhãn trạng thái · "lần đầu · lần cuối · ai ghi nhận / đánh dấu" ·
 * ba mốc · nút. MỚI: nút chính "Ghi nhận"; ĐANG XỬ LÝ: nút chính "Đã xử lý"; CHỜ TỦ: không nút (lời khai đã có).
 * "Đã xử lý" hỏi lại MỘT lần ngay tại chỗ (không hộp thoại trình duyệt), kèm ô ghi chú tuỳ chọn, và NÓI TRƯỚC hồ sơ
 * sẽ đóng ngay hay chờ tủ (`xuLyChoTuXacNhan`).
 * SCHEDULE_MISMATCH — HAI CHIỀU ĐỒNG BỘ (TK-26, lee 10/10), chỉ ai được sửa lịch tuyến đó:
 *   "Lấy lịch tủ về máy chủ"  tủ giữ nguyên, đèn không đổi; bản máy chủ theo tủ.
 *   "Đẩy lịch xuống tủ"       tủ chạy theo bản máy chủ — ĐỔI đèn.
 * Cả hai HỎI LẠI và kể trước, ngắn, từng ngõ sẽ đổi gì (`cauDongBo`, đọc từ câu lệch máy chủ đã ghi).
 */
function DongVanDe({ a, dayLichDuoc, onGhiNhan, onXuLy, onDayLich, onLayLich, dangBan }: {
  a: Alarm; dayLichDuoc: boolean; onGhiNhan: () => void; onXuLy: (ghiChu: string) => void;
  onDayLich: () => void; onLayLich: () => void; dangBan: boolean;
}) {
  const pm = phanMuc(a.code, a.mucDo);
  const loai = loaiMuc(a);
  const tt = trangThaiHoSo(a);
  const laLich = pm.ma === 'SCHEDULE_MISMATCH';
  const choTu = xuLyChoTuXacNhan(pm.ma || a.code);
  const [hoi, setHoi] = useState<null | 'xu-ly' | ChieuDongBo>(null);
  const [ghiChu, setGhiChu] = useState('');
  const lanCuoi = a.lastSeenAt ?? null;
  /* Đã đánh dấu mà nguồn vẫn còn nhắc SAU lúc đánh dấu (> 1 phút) ⇒ nói ra: người trực tưởng xong, tủ nói chưa. */
  const vanCon = tt === 'cho-tu' && !!lanCuoi && !!a.resolvedAt && new Date(lanCuoi).getTime() - new Date(a.resolvedAt).getTime() > 60_000;
  const nhan = tt === 'cho-tu'
    ? { chu: 'CHỜ TỦ XÁC NHẬN', lop: 'border-tot/50 bg-tot/10 text-tot' }
    : tt === 'dang-xu-ly'
      ? { chu: 'ĐANG XỬ LÝ', lop: 'border-nhan/50 bg-nhan/15 text-nhan' }
      : loai === 'khan' ? { chu: 'KHẨN', lop: 'border-nguy/50 bg-nguy/15 text-nguy' }
        : loai === 'mat' ? { chu: 'MẤT KẾT NỐI', lop: 'border-vien-manh bg-nen-2 text-chu-2' }
          : { chu: 'MỚI', lop: 'border-canh/50 bg-canh-nen text-canh' };
  return (
    <div
      data-alarm-id={a.id} data-ma={pm.ma || a.code} data-muc={loai} data-tt={tt}
      className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-2 border-l-[3px] py-2 pl-3 ${LOP_VIEN[loai]}`}
    >
      {/* ⚠ Cột chữ `flex-1 min-w-[240px]`, cột nút `ml-auto` + wrap: ở cột phải 560 px nút xuống dòng dưới chữ thay vì bóp chữ. */}
      <div className="min-w-[240px] flex-1">
        <p className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-chu-1">
          <span data-ten-viec>{tenViec(a.code)}</span><span className="text-chu-3">{ngoacNgoRa(a)}</span>
          <span className={`rounded-thuoc border px-2 py-0.5 text-xs font-semibold tracking-wide ${nhan.lop}`} data-nhan-tt>{nhan.chu}</span>
        </p>
        <p className="m-0 mt-0.5 text-xs text-chu-3" data-moc-thoi-gian>
          Lần đầu <span className="tabular-nums">{gioNgan(a.raisedAt)}</span>
          {lanCuoi && <> · lần cuối <b className={`font-medium tabular-nums ${vanCon ? 'text-canh' : 'text-chu-2'}`}>{gioNguoi(lanCuoi)}</b></>}
          {' · '}{tuoiNgan(a.raisedAt)}
          {a.ackedAt && tt !== 'cho-tu' && <> · {tenNguoi(a.ackedBy)} ghi nhận <span className="tabular-nums">{gioNguoi(a.ackedAt)}</span></>}
          {tt === 'cho-tu' && a.resolvedAt && <> · {tenNguoi(a.resolvedBy)} đánh dấu đã xử lý <span className="tabular-nums">{gioNguoi(a.resolvedAt)}</span></>}
          {vanCon && <span className="text-canh"> · tủ vẫn báo sau lúc đánh dấu</span>}
          {a.note && <> — <q className="text-chu-2">{a.note}</q></>}
        </p>
      </div>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5">
        <BaMoc daGhiNhan={!!a.ackedAt} xuLy={tt === 'cho-tu' ? 'cho-tu' : 'chua'} />
        {hoi === null && (
          <span className="flex flex-wrap gap-1.5" data-nut-ho-so>
            {laLich && (
              <>
                {dayLichDuoc && tt !== 'cho-tu' && (
                  <>
                    <Button size="sm" variant="primary" className={NUT_DT} onClick={() => setHoi('tu-ve-may-chu')} disabled={dangBan}>Lấy lịch tủ về máy chủ</Button>
                    <Button size="sm" variant="secondary" className={NUT_DT} onClick={() => setHoi('may-chu-xuong-tu')} disabled={dangBan}>Đẩy lịch xuống tủ</Button>
                  </>
                )}
                <Link to={`/lich/${a.cabinetId}`} className="inline-flex min-h-[36px] items-center rounded-nut border border-vien-manh bg-nen-1 px-2.5 text-xs text-chu-1 hover:bg-nen-2 sm:min-h-0 sm:py-1.5">Xem lịch tủ</Link>
              </>
            )}
            {tt !== 'cho-tu' && (
              <CoQuyen khoa="nhan_canh_bao" an>
                {tt === 'moi' && (
                  <Button size="sm" variant={laLich && dayLichDuoc ? 'secondary' : 'primary'} className={NUT_DT} onClick={onGhiNhan} disabled={dangBan} icon={<CheckCheck className="h-3.5 w-3.5" />}>Ghi nhận</Button>
                )}
                <Button size="sm" variant={tt === 'dang-xu-ly' ? 'primary' : 'secondary'} className={NUT_DT} onClick={() => setHoi('xu-ly')} disabled={dangBan}>Đã xử lý</Button>
              </CoQuyen>
            )}
          </span>
        )}
      </div>
      {hoi === 'xu-ly' && (
        <div className="w-full rounded-nut border border-tot/40 bg-tot/5 p-2.5 text-xs text-chu-1" data-hoi-xu-ly>
          <p className="m-0">{choTu
            ? 'Ghi tên bạn là người đã xử lý. Hồ sơ còn mở tới khi tủ báo bình thường lại, rồi tự đóng.'
            : 'Đóng hồ sơ này và ghi tên bạn là người xử lý.'}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              value={ghiChu} onChange={(e) => setGhiChu(e.target.value)} maxLength={300}
              placeholder="Ghi chú (không bắt buộc)" aria-label="Ghi chú xử lý"
              className="min-h-[36px] min-w-[200px] flex-1 rounded-nut border border-vien bg-nen-1 px-2.5 text-sm text-chu-1 placeholder:text-chu-3 sm:min-h-0 sm:py-1.5"
            />
            <Button size="sm" variant="success" className={NUT_DT} onClick={() => { setHoi(null); onXuLy(ghiChu); setGhiChu(''); }} disabled={dangBan}>Xác nhận</Button>
            <Button size="sm" variant="ghost" className={NUT_DT} onClick={() => setHoi(null)}>Thôi</Button>
          </div>
        </div>
      )}
      {(hoi === 'tu-ve-may-chu' || hoi === 'may-chu-xuong-tu') && (
        <HoiDongBo chieu={hoi} message={a.message} dangBan={dangBan}
          onDongY={() => { const c = hoi; setHoi(null); if (c === 'tu-ve-may-chu') onLayLich(); else onDayLich(); }}
          onThoi={() => setHoi(null)} />
      )}
    </div>
  );
}

/**
 * Hộp hỏi lại của MỘT chiều đồng bộ: câu hỏi · từng ngõ đổi gì (ngắn) · cái gì GIỮ · hai nút.
 * ⚠ Chiều "đẩy xuống" đổi đèn ngoài đường ⇒ viền nhấn + câu hỏi nói rõ "ghi đè"; chiều "lấy về" không đổi đèn.
 */
function HoiDongBo({ chieu, message, dangBan, onDongY, onThoi }: {
  chieu: ChieuDongBo; message: string | null | undefined; dangBan: boolean; onDongY: () => void; onThoi: () => void;
}) {
  const dong = cauDongBo(message, chieu);
  const veMayChu = chieu === 'tu-ve-may-chu';
  return (
    <div className="w-full rounded-nut border border-nhan/40 bg-nhan/5 p-2.5 text-xs text-chu-1" data-hoi-dong-bo={chieu}>
      <p className="m-0 font-medium">{veMayChu ? 'Lấy lịch đang chạy trong tủ làm bản máy chủ?' : 'Ghi đè lịch trong tủ bằng bản máy chủ?'}</p>
      {dong.length > 0 && (
        <ul className="m-0 mt-1 list-none space-y-0.5 p-0 text-chu-2">
          {dong.map((d) => <li key={d}>{d}</li>)}
        </ul>
      )}
      <p className="m-0 mt-1 text-chu-3">{CAU_KET_DONG_BO[chieu]}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="primary" className={NUT_DT} onClick={onDongY} disabled={dangBan}>{veMayChu ? 'Lấy lịch' : 'Đẩy lịch'}</Button>
        <Button size="sm" variant="ghost" className={NUT_DT} onClick={onThoi}>Thôi</Button>
      </div>
    </div>
  );
}

/**
 * Ba mốc Tạo → Ghi nhận → Đã xử lý (học Versus): chấm tô = đã qua, rỗng = chưa tới. `cho-tu`: mốc ba tô NỬA
 * (vòng xanh, lõi rỗng) — người đã khai, tủ chưa xác nhận. Vẽ SVG, không nền.
 */
function BaMoc({ daGhiNhan, xuLy }: { daGhiNhan: boolean; xuLy: 'chua' | 'cho-tu' }) {
  const moc: { ten: string; kieu: 'tron' | 'nua' | 'rong' }[] = [
    { ten: 'Tạo', kieu: 'tron' },
    { ten: 'Ghi nhận', kieu: daGhiNhan ? 'tron' : 'rong' },
    { ten: xuLy === 'cho-tu' ? 'Đã xử lý · chờ tủ' : 'Đã xử lý', kieu: xuLy === 'cho-tu' ? 'nua' : 'rong' },
  ];
  return (
    <span className="hidden items-center gap-1 whitespace-nowrap text-xs text-chu-3 lg:inline-flex" aria-hidden="true" data-ba-moc>
      {moc.map((m, i) => (
        <span key={m.ten} className="inline-flex items-center gap-1">
          {i > 0 && <span className="inline-block h-px w-3 bg-vien-manh" />}
          <svg width="8" height="8" viewBox="0 0 8 8" className={m.kieu === 'nua' ? 'text-tot' : m.kieu === 'tron' ? 'text-nhan' : 'text-chu-3'}>
            <circle cx="4" cy="4" r="3" fill={m.kieu === 'tron' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={m.kieu === 'nua' ? 1.8 : 1.2} />
          </svg>
          <span className={m.kieu !== 'rong' ? 'text-chu-2' : ''}>{m.ten}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * "Đã xử lý · 7 ngày qua" — chữ xanh, dồn cuối; 5 dòng, bấm mở thêm. Câu nói ĐỦ hai nhân chứng khi có:
 *   người đánh dấu + tủ báo bình thường lại  ·  chỉ tủ (máy chủ tự đóng)  ·  chỉ người (mã không tự đóng được).
 */
function DaXuLy({ id, ds, dangTai }: { id: string; ds: Alarm[] | undefined; dangTai: boolean }) {
  const [moHet, setMoHet] = useState(false);
  const hang = ds ?? [];
  const hien = moHet ? hang : hang.slice(0, SO_DA_XU_LY_HIEN);
  const cau = (a: Alarm): string => {
    const dong = a.clearedAt ? gioNguoi(a.clearedAt) : '';
    if (a.resolvedHow === 'tu-dong') {
      return a.resolvedAt
        ? `${tenNguoi(a.resolvedBy)} đánh dấu ${gioNguoi(a.resolvedAt)} · tủ báo bình thường lại ${dong}`
        : `tủ báo bình thường lại ${dong} — máy chủ tự đóng`;
    }
    if (a.resolvedHow === 'nguoi') return `${tenNguoi(a.resolvedBy)} đóng hồ sơ ${dong}`;
    return `đóng ${dong}`;
  };
  return (
    <div id={id} className="px-4 pb-3" data-da-xu-ly>
      <p className="m-0 flex items-center gap-2 py-2 text-xs text-chu-3">
        <span className="h-px flex-1 bg-vien" />Đã xử lý · 7 ngày qua<span className="h-px flex-1 bg-vien" />
      </p>
      {dangTai && <Spinner label="Đang tải hồ sơ đã đóng…" />}
      {!dangTai && hang.length === 0 && <p className="m-0 py-1 text-center text-xs text-chu-3">Không có hồ sơ nào đóng trong 7 ngày qua.</p>}
      {hien.map((a) => (
        <div key={a.id} className="border-l-[3px] border-tot/45 py-1.5 pl-3 text-sm" data-alarm-id={a.id} data-dong-boi={a.resolvedHow ?? 'cu'}>
          <span className="text-tot">✓ {a.cabinet?.name ?? a.cabinetId} · {tenViec(a.code)}</span>
          <span className="ml-2 rounded-thuoc border border-tot/40 bg-tot/10 px-2 py-0.5 text-xs font-semibold tracking-wide text-tot">ĐÃ XỬ LÝ</span>
          <span className="ml-2 text-xs text-tot/75">{cau(a)}{a.note ? ` — “${a.note}”` : ''}</span>
        </div>
      ))}
      {hang.length > SO_DA_XU_LY_HIEN && (
        <button type="button" onClick={() => setMoHet((v) => !v)} className="mt-1 min-h-[36px] text-xs text-nhan hover:underline">
          {moHet ? 'Thu gọn' : `Xem thêm ${hang.length - SO_DA_XU_LY_HIEN} hồ sơ`}
        </button>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- bộ phận con */

/** Cột theo tuyến trong thẻ Tổng số tủ (lee: "từng số tủ theo tuyến biểu đồ dạng cột"). Tối đa 8 cột, cao 44 px. */
function CotTheoTuyen({ ds }: { ds: OverviewRouteCard[] }) {
  const tuyen = ds.filter((r) => r.routeId !== null).slice(0, 8);
  if (tuyen.length === 0) return null;
  const max = Math.max(...tuyen.map((r) => r.total), 1);
  return (
    <div className="text-right" data-cot-tuyen title={tuyen.map((r) => `${r.tenDayDu}: ${r.total} tủ`).join('\n')}>
      <svg width={tuyen.length * 16} height="44" viewBox={`0 0 ${tuyen.length * 16} 44`} aria-hidden="true" focusable="false" className="block ml-auto">
        {tuyen.map((r, i) => {
          const h = Math.max(4, Math.round((r.total / max) * 44));
          return <rect key={r.routeId} x={i * 16 + 1} y={44 - h} width="12" height={h} rx="2" className={r.online < r.total ? 'text-canh' : 'text-nhan'} fill="currentColor" />;
        })}
      </svg>
      <p className="m-0 mt-1 text-xs tabular-nums text-chu-3">{tuyen.map((r) => r.total).join(' · ')} tủ / tuyến</p>
    </div>
  );
}

/** Dòng tuyến: tên · "a/b nối · N việc" · thanh mảnh xanh theo tỷ lệ nối. Chấm vàng nếu có việc, xám nếu chỉ có tủ mất kết nối; số nối tô vàng khi thiếu (không đỏ — tệp 29). */
function RouteCard({ route, soViec }: { route: OverviewRouteCard; soViec: number }) {
  const laChuaGan = route.routeId === null;
  const ptNoi = route.total > 0 ? Math.round((route.online / route.total) * 100) : 0;
  const coMat = !laChuaGan && route.online < route.total;
  return (
    <div className={laChuaGan ? 'text-chu-3' : ''} data-dong-tuyen={route.routeId ?? 'chua-gan'}>
      <div className="flex items-start gap-1.5">
        {!laChuaGan && (coMat || soViec > 0) && <span className="mt-1"><Cham mau={soViec > 0 ? 'text-canh' : 'text-chu-3'} px={8} /></span>}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className={`m-0 min-w-0 truncate text-sm font-semibold ${laChuaGan ? 'text-chu-2' : 'text-chu-1'}`} title={route.tenDayDu}>{route.tenDayDu}</p>
            {!laChuaGan && (
              <span className={`shrink-0 text-xs tabular-nums ${coMat ? 'text-canh' : 'text-chu-3'}`}>
                {route.online}/{route.total} nối{soViec > 0 ? ` · ${soViec} việc` : ''}
              </span>
            )}
          </div>
          {laChuaGan ? (
            <p className="m-0 text-xs text-chu-3">{route.total} tủ — cần gán tuyến trước khi vận hành</p>
          ) : (
            <>
              <div className="mt-1.5"><ThanhManh pt={ptNoi} lop="text-tot" /></div>
              <p className="m-0 mt-1 truncate text-xs text-chu-3">
                {route.khongDem !== undefined && route.khongDem > 0
                  ? `${route.on} ngõ ra bật trên ${route.total - route.khongDem}/${route.total} tủ nối được`
                  : `${route.on} ngõ ra bật`}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Thẻ thu gọn được (TK-24). TK-25: `macDinhMo` — hình lee duyệt 10/10 vẽ "Hoạt động gần đây" ĐANG MỞ (sau khi
 * đã lọc còn sự kiện có nghĩa, ô không còn dài). Người dùng gập thì nhớ (`localStorage`, bọc try/catch).
 */
function TheThuGon({ khoa, title, phu, actions, children, macDinhMo = false }: {
  khoa: string; title: string; phu?: string; actions?: React.ReactNode; children: React.ReactNode; macDinhMo?: boolean;
}) {
  const [mo, setMo] = useState<boolean>(() => {
    try { const v = localStorage.getItem(khoa); return v === null ? macDinhMo : v === '1'; } catch { return macDinhMo; }
  });
  const doi = () => {
    setMo((v) => { const m = !v; try { localStorage.setItem(khoa, m ? '1' : '0'); } catch { /* bỏ qua */ } return m; });
  };
  return (
    <section className="card min-w-0" data-thu-gon={mo ? 'mo' : 'dong'}>
      <header className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <button type="button" onClick={doi} aria-expanded={mo} aria-controls={`${khoa}-than`}
          className="flex min-h-[36px] min-w-0 flex-1 items-center gap-2 rounded-nut text-left hover:text-nhan">
          <ChevronDown className={`h-4 w-4 shrink-0 text-chu-3 transition-transform ${mo ? '' : '-rotate-90'}`} aria-hidden="true" />
          <h2 className="m-0 whitespace-nowrap text-sm font-semibold text-chu-1">{title}</h2>
          {!mo && phu && <span className="hidden truncate text-xs text-chu-3 xl:inline">· {phu}</span>}
        </button>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      {mo && <div id={`${khoa}-than`} className="border-t border-vien p-4">{children}</div>}
    </section>
  );
}

/** Hoạt động gần đây — CHỈ sự kiện trong `HOAT_DONG_CO_NGHIA`, 6 mục mới nhất, hai cột từ 640 px. */
function HoatDongGanDay({ ds, dangTai, loi, tenTu }: {
  ds: AuditRow[] | undefined; dangTai: boolean; loi: boolean; tenTu: Map<string, string>;
}) {
  if (dangTai) return <Spinner label="Đang tải nhật ký…" />;
  if (loi) return <p className="py-3 text-sm text-chu-2">Không tải được nhật ký thao tác.</p>;
  /* Máy chủ đã lọc theo `HOAT_DONG_CO_NGHIA` (`?actions=`); lọc lại ở đây là chốt thứ hai cho máy chủ bản cũ
     (bỏ qua tham số lạ ⇒ trả mọi mã). */
  const hang = [...(ds ?? [])]
    .filter((r) => HOAT_DONG_CO_NGHIA.includes(r.action))
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    .slice(0, SO_HOAT_DONG);
  if (hang.length === 0) return <p className="py-3 text-sm text-chu-2">Chưa có sự kiện đáng chú ý nào được ghi.</p>;
  const LOP = { xam: 'text-chu-3', xanh: 'text-tot', vang: 'text-canh' } as const;
  return (
    <ul className="tq-hd m-0 list-none p-0" data-hoat-dong>
      {hang.map((r) => {
        const idTu = r.target?.startsWith('cabinet:') ? r.target.slice(8) : null;
        const idTuAlarm = !idTu && typeof (r.detail as { cabinetId?: unknown } | null)?.cabinetId === 'string' ? (r.detail as { cabinetId: string }).cabinetId : null;
        const thietBi = r.target?.startsWith('thiet-bi:') ? r.target.slice(9) : null;
        const d = dongHoatDong(r.action, r.detail, (idTu ?? idTuAlarm) ? (tenTu.get((idTu ?? idTuAlarm)!) ?? null) : null);
        const ai = r.user ? (r.user.fullName || r.user.username) : (thietBi ? `Thiết bị ${thietBi}` : aiDaLam(r.action, false));
        return (
          <li key={r.id} className="flex items-start gap-2 border-b border-vien pb-2" data-hoat-dong-muc={r.id}>
            <span className="w-[88px] shrink-0 text-xs tabular-nums text-chu-3">{gioNguoi(r.at)}</span>
            <span className="mt-1.5"><Cham mau={LOP[d.loai]} px={8} /></span>
            <p className={`m-0 min-w-0 flex-1 text-sm ${d.loai === 'vang' ? 'text-canh' : 'text-chu-1'}`} title={d.cau}>
              <b className="font-medium">{ai}</b> <span className="text-chu-2">{d.cau}</span>
            </p>
          </li>
        );
      })}
    </ul>
  );
}
